/** How {@link normalizeText} folds input, from lightest to heaviest. Levels do not nest: one uppercases, the others lowercase. */
export enum TextFoldLevel {
    /** Width and whitespace only. Keeps case, accents and punctuation. */
    WIDTH_AND_WHITESPACE = "WIDTH_AND_WHITESPACE",
    /** Width and whitespace, then uppercase. Keeps accents and punctuation. */
    WIDTH_WHITESPACE_AND_UPPERCASE = "WIDTH_WHITESPACE_AND_UPPERCASE",
    /** Width, whitespace, Latin diacritics, then lowercase. Keeps punctuation and non-Latin marks. */
    WHITESPACE_AND_CASING = "WHITESPACE_AND_CASING",
    /** Trailing sign-off punctuation, then everything {@link TextFoldLevel.WHITESPACE_AND_CASING} folds. Keeps internal punctuation. */
    WHITESPACE_CASING_AND_TRAILING_PUNCTUATION = "WHITESPACE_CASING_AND_TRAILING_PUNCTUATION",
    /** Everything {@link TextFoldLevel.WHITESPACE_AND_CASING} folds, then every separator and punctuation mark. Keeps letters, digits and marks. */
    SEPARATORS_WHITESPACE_PUNCTUATION_AND_CASING = "SEPARATORS_WHITESPACE_PUNCTUATION_AND_CASING",
}

const TRAILING_SENTENCE_PUNCTUATION = /[.,!?;:。、！？]+$/u;

const foldWidthAndWhitespace = (raw: string) => raw.normalize("NFKC").replace(/\s+/gu, " ").trim();

const foldLatinAccentsAndCase = (raw: string) =>
    foldWidthAndWhitespace(raw)
        .normalize("NFD")
        .replace(/(\p{Script=Latin})\p{Mn}+/gu, "$1")
        .normalize("NFC")
        .toLowerCase();

/**
 * Normalizes text for comparison. NFKC (not NFKD) so width folds without decomposing the combining
 * marks Gurmukhi and kana spell words with; only marks on a Latin base letter are stripped.
 */
export function normalizeText(raw: string, level: TextFoldLevel): string {
    switch (level) {
        case TextFoldLevel.WIDTH_AND_WHITESPACE:
            return foldWidthAndWhitespace(raw);
        case TextFoldLevel.WIDTH_WHITESPACE_AND_UPPERCASE:
            // `toUpperCase`, never `toLocaleUpperCase`: locale-sensitive casing would make matching depend on the server's default locale.
            return foldWidthAndWhitespace(raw).toUpperCase();
        case TextFoldLevel.WHITESPACE_AND_CASING:
            return foldLatinAccentsAndCase(raw);
        case TextFoldLevel.WHITESPACE_CASING_AND_TRAILING_PUNCTUATION:
            // Strips before folding, so whitespace ahead of the punctuation is trimmed by the fold.
            return foldLatinAccentsAndCase(raw.replace(TRAILING_SENTENCE_PUNCTUATION, ""));
        case TextFoldLevel.SEPARATORS_WHITESPACE_PUNCTUATION_AND_CASING:
            return foldLatinAccentsAndCase(raw).replace(/[^\p{L}\p{N}\p{M}]/gu, "");
    }
}
